import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, consultingApi } from '../utils/api';

const STAGE_OPTIONS = [
  { value: 'all', label: 'All stages' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'pilot', label: 'Pilot' },
  { value: 'implementation', label: 'Implementation' },
  { value: 'retainer', label: 'Retainer' },
  { value: 'completed', label: 'Completed' },
];

const stageBadgeCls = (stage) => {
  const map = {
    prospect: 'bg-gray-800 text-gray-300',
    assessment: 'bg-blue-900/40 text-blue-300',
    pilot: 'bg-yellow-900/40 text-yellow-300',
    implementation: 'bg-teal-900/40 text-teal-300',
    retainer: 'bg-green-900/40 text-green-300',
    completed: 'bg-purple-900/40 text-purple-300',
  };
  return map[stage] || 'bg-gray-800 text-gray-400';
};

const inp = 'border border-gray-700 rounded px-3 py-2 bg-gray-900 text-white text-sm w-full focus:outline-none focus:border-gray-500';
const th = 'text-left text-xs font-medium text-gray-400 uppercase tracking-wider py-2 px-3';
const td = 'py-2 px-3 text-sm text-gray-200';

const SALES_CACHE_PREFIX = 'wowrolla_admin_sales_cache_v1';

const readCache = (key) => {
  try {
    const cached = JSON.parse(localStorage.getItem(key) || 'null');
    return cached && typeof cached === 'object' ? cached : null;
  } catch {
    return null;
  }
};

const writeCache = (key, payload) => {
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // ignore storage issues
  }
};

const AdminSalesManagementPage = () => {
  const [adminKey, setAdminKey] = useState('');
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('all');
  const [overview, setOverview] = useState(null);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const loadSalesData = async (key) => {
    if (!key) return;
    setLoading(true);
    setError('');
    const cacheKey = `${SALES_CACHE_PREFIX}:${key}`;
    const cached = readCache(cacheKey);
    if (cached?.overview || cached?.clients) {
      setOverview(cached.overview || null);
      setClients(Array.isArray(cached.clients) ? cached.clients : []);
      setLoading(false);
    }
    try {
      const [overviewRes, clientsRes] = await Promise.all([
        adminApi.getSalesOverview(),
        consultingApi.getClients(key),
      ]);
      const nextOverview = overviewRes.data || null;
      const nextClients = Array.isArray(clientsRes.data) ? clientsRes.data : [];
      setOverview(nextOverview);
      setClients(nextClients);
      writeCache(cacheKey, { overview: nextOverview, clients: nextClients, updatedAt: new Date().toISOString() });
    } catch (loadError) {
      setError(loadError?.response?.data?.detail || 'Failed to load sales data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const stored = localStorage.getItem('wowrolla_admin_key') || '';
    if (stored) {
      setAdminKey(stored);
      loadSalesData(stored);
      return;
    }
    setLoading(false);
  }, []);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (clients || []).filter((client) => {
      const matchesStage = stage === 'all' ? true : (client.stage || 'prospect') === stage;
      const haystack = `${client.biz_name || ''} ${client.contact_name || ''} ${client.email || ''}`.toLowerCase();
      const matchesSearch = !needle || haystack.includes(needle);
      return matchesStage && matchesSearch;
    });
  }, [clients, search, stage]);

  const saveAdminKey = () => {
    const nextKey = (adminKey || '').trim();
    if (!nextKey) return;
    localStorage.setItem('wowrolla_admin_key', nextKey);
    loadSalesData(nextKey);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Admin Sales Management</h2>
          <p className="text-gray-400 text-sm">Consulting pipeline overview</p>
        </div>
        <Link to="/admin/dashboard" className="border border-gray-600 hover:bg-gray-800 text-white rounded px-3 py-1.5 text-sm">
          Back to Admin Dashboard
        </Link>
      </div>

      {/* Admin key entry */}
      {!adminKey && (
        <div className="border border-gray-700 rounded-lg p-6 space-y-3">
          <p className="font-semibold text-white">Connect Consulting Data</p>
          <p className="text-sm text-gray-400">Provide your WOWROLLA_ADMIN_KEY once to load client pipeline and sales metrics.</p>
          <div className="flex gap-3 items-end">
            <div className="flex-1 max-w-md">
              <input className={inp} placeholder="Paste WOWROLLA_ADMIN_KEY" value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)} />
            </div>
            <button className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm" onClick={saveAdminKey}>
              Load Sales Data
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-red-400 text-sm border border-red-900/40 rounded p-3">{error}</p>}

      {/* KPI row */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Clients', value: overview.total_clients || 0 },
            { label: 'Pilots', value: overview.pilot_clients || 0 },
            { label: 'Implementation', value: overview.implementation_clients || 0 },
            { label: 'Retainers', value: overview.retainer_clients || 0 },
          ].map(({ label, value }) => (
            <div key={label} className="border border-gray-700 rounded-lg p-4">
              <p className="text-xs text-gray-400">{label}</p>
              <p className="text-2xl font-bold text-white">{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Pipeline table */}
      <div className="border border-gray-700 rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h4 className="text-base font-semibold text-white">Client Pipeline</h4>
          <div className="flex gap-2">
            <select className="border border-gray-700 rounded px-3 py-2 bg-gray-900 text-white text-sm focus:outline-none"
              value={stage} onChange={(e) => setStage(e.target.value)}>
              {STAGE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            <input className="border border-gray-700 rounded px-3 py-2 bg-gray-900 text-white text-sm w-52 focus:outline-none"
              placeholder="Search clients" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                {['Business', 'Contact', 'Industry', 'Stage'].map((h) => (
                  <th key={h} className={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((client) => (
                <tr key={client.id} className="border-b border-gray-800 hover:bg-gray-900/40">
                  <td className={td}>{client.biz_name}</td>
                  <td className={td}>
                    <p>{client.contact_name}</p>
                    <p className="text-xs text-gray-400">{client.email}</p>
                  </td>
                  <td className={td}>{client.industry}</td>
                  <td className={td}>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${stageBadgeCls(client.stage)}`}>
                      {client.stage || 'prospect'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <p className="text-sm text-gray-400">Loading sales data...</p>}
        {!loading && filtered.length === 0 && <p className="text-sm text-gray-400">No clients found for current filters.</p>}
      </div>
    </div>
  );
};

export default AdminSalesManagementPage;

